import { useCallback, useMemo, useState } from "react";

import { useSimulation } from "./hooks/useSimulation";
import { WorldCanvas } from "./rendering/WorldCanvas";
import type { BuildingRecord, CreatureRecord } from "./simulation/world";

const speedOptions = [1, 4, 16] as const;

function formatElapsedTime(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;
}

function titleCase(value: string) {
  return value
    .replaceAll("-", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function needPercent(value: number) {
  return `${Math.round(value)}%`;
}

function CreatureInspector({
  creature,
  world,
  onClose,
}: {
  creature: CreatureRecord;
  onClose: () => void;
  world: NonNullable<ReturnType<typeof useSimulation>["world"]>;
}) {
  const currentTile = world.tiles.find((tile) => tile.id === creature.tileId);
  const targetTile =
    creature.currentTargetTileId !== null
      ? world.tiles.find((tile) => tile.id === creature.currentTargetTileId)
      : null;
  const targetResource =
    creature.currentTargetResourceNodeId !== null
      ? world.resourceNodes.find(
          (node) => node.id === creature.currentTargetResourceNodeId,
        )
      : null;

  return (
    <section className="creature-inspector" aria-label="Selected creature">
      <div className="inspector-heading">
        <div>
          <p className="eyebrow">Selected creature</p>
          <h2>{creature.name}</h2>
        </div>
        <button className="icon-control" type="button" onClick={onClose}>
          ×
        </button>
      </div>
      <p className="creature-subtitle">
        {creature.alive ? "" : "Dead · "}
        {creature.ageYears}-year-old {creature.sex} {creature.lifeStage} ·{" "}
        {titleCase(creature.kind)} ·{" "}
        {creature.continentId === "west" ? "West" : "East"}
      </p>

      <div className="inspector-status">
        <span className={`mood-chip mood-${creature.mood.label}`}>
          {titleCase(creature.mood.label)}
        </span>
        <span>{titleCase(creature.action)}</span>
        <span>{titleCase(creature.currentJob)}</span>
      </div>

      <p className="inspector-reason">{creature.mood.reason}</p>
      <p className="inspector-reason">{creature.recentDecisions[0]?.reason}</p>
      {creature.pregnancy ? (
        <p className="inspector-reason">
          Pregnant · {creature.pregnancy.remainingDays} days until birth
        </p>
      ) : null}
      {creature.parentIds.length > 0 ? (
        <p className="inspector-reason">
          Child of{" "}
          {creature.parentIds
            .map(
              (id) =>
                world.creatures.find((parent) => parent.id === id)?.name ?? id,
            )
            .join(" and ")}
        </p>
      ) : null}

      <div className="location-lines">
        <div>
          <span>Location</span>
          <strong>
            {currentTile ? `${currentTile.x}, ${currentTile.y}` : "Unknown"}
          </strong>
        </div>
        <div>
          <span>Target</span>
          <strong>
            {targetResource?.subtype ??
              (targetTile ? `${targetTile.x}, ${targetTile.y}` : "None")}
          </strong>
        </div>
        <div>
          <span>Household</span>
          <strong>{creature.householdId ?? "None"}</strong>
        </div>
      </div>

      <div className="need-list" aria-label="Current needs">
        {(
          [
            ["health", creature.health],
            ["water", creature.needs.water],
            ["food", creature.needs.food],
            ["rest", creature.needs.rest],
            ["warmth", creature.needs.warmth],
            ["safety", creature.needs.safety],
          ] as const
        ).map(([need, value]) => (
          <div className="need-row" key={need}>
            <div>
              <span>{titleCase(need)}</span>
              <strong>{needPercent(value)}</strong>
            </div>
            <div className="need-track">
              <span
                className={`need-fill need-${need}`}
                style={{ width: `${value}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="attribute-grid">
        <div>
          <span>Strength</span>
          <strong>{Math.round(creature.traits.strength * 100)}</strong>
        </div>
        <div>
          <span>Intelligence</span>
          <strong>{Math.round(creature.traits.intelligence * 100)}</strong>
        </div>
        <div>
          <span>Aggression</span>
          <strong>{Math.round(creature.traits.aggression * 100)}</strong>
        </div>
        <div>
          <span>Risk tolerance</span>
          <strong>{Math.round(creature.traits.riskTolerance * 100)}</strong>
        </div>
      </div>

      <div className="inventory-line">
        <span>Carried</span>
        <strong>
          {creature.inventory.water} water · {creature.inventory.food} food
        </strong>
      </div>
    </section>
  );
}

function BuildingInspector({
  building,
  world,
  onClose,
}: {
  building: BuildingRecord;
  onClose: () => void;
  world: NonNullable<ReturnType<typeof useSimulation>["world"]>;
}) {
  const tile = world.tiles.find((entry) => entry.id === building.tileId);
  const workers = building.workerCreatureIds
    .map((id) => world.creatures.find((creature) => creature.id === id))
    .filter((creature): creature is CreatureRecord => creature !== undefined);

  return (
    <section className="creature-inspector" aria-label="Selected station">
      <div className="inspector-heading">
        <div>
          <p className="eyebrow">Selected station</p>
          <h2>{building.label}</h2>
        </div>
        <button className="icon-control" type="button" onClick={onClose}>
          ×
        </button>
      </div>
      <p className="creature-subtitle">
        {titleCase(building.kind)} ·{" "}
        {building.continentId === "west" ? "West" : "East"} camp
      </p>

      <div className="inspector-status">
        <span className="mood-chip">{titleCase(building.kind)}</span>
        <span>{workers.length} at work</span>
      </div>

      <p className="inspector-reason">{building.purpose}</p>
      <p className="inspector-reason">Making: {building.making}</p>

      <div className="location-lines">
        <div>
          <span>Location</span>
          <strong>{tile ? `${tile.x}, ${tile.y}` : "Unknown"}</strong>
        </div>
        <div>
          <span>Stored</span>
          <strong>{building.storedUnits} units</strong>
        </div>
      </div>

      <div className="inventory-line">
        <span>People here</span>
        <strong>
          {workers.length === 0
            ? "None"
            : workers.map((creature) => creature.name).join(", ")}
        </strong>
      </div>
    </section>
  );
}

export function App() {
  const { state, world, pause, resume, reset, setSpeed } = useSimulation();
  const [selectedCreatureId, setSelectedCreatureId] = useState<string | null>(
    null,
  );
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(
    null,
  );
  const formattedSeed = useMemo(
    () => state.seed.toString(16).toUpperCase().padStart(8, "0"),
    [state.seed],
  );
  const effectiveSelectedCreatureId = world?.creatures.some(
    (creature) => creature.id === selectedCreatureId,
  )
    ? selectedCreatureId
    : null;
  const selectedCreature = useMemo(
    () =>
      world?.creatures.find(
        (creature) => creature.id === effectiveSelectedCreatureId,
      ) ?? null,
    [effectiveSelectedCreatureId, world],
  );
  const effectiveSelectedBuildingId = world?.buildings.some(
    (building) => building.id === selectedBuildingId,
  )
    ? selectedBuildingId
    : null;
  const selectedBuilding = useMemo(
    () =>
      world?.buildings.find(
        (building) => building.id === effectiveSelectedBuildingId,
      ) ?? null,
    [effectiveSelectedBuildingId, world],
  );

  const selectCreature = useCallback((creatureId: string) => {
    setSelectedCreatureId(creatureId);
    setSelectedBuildingId(null);
  }, []);

  const selectBuilding = useCallback((buildingId: string) => {
    setSelectedBuildingId(buildingId);
    setSelectedCreatureId(null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedCreatureId(null);
    setSelectedBuildingId(null);
  }, []);

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand-lockup">
          <span className="brand-mark" aria-hidden="true">
            F
          </span>
          <div>
            <p className="eyebrow">Civilization simulation</p>
            <h1>First Fires</h1>
          </div>
        </div>

        <div className="world-identity" aria-label="Current generated world">
          <span>World</span>
          <strong>Emberwake</strong>
          <code>#{formattedSeed}</code>
        </div>
      </header>

      <section className="workspace">
        <aside className="side-panel" aria-label="World overview">
          <div className="panel-heading">
            <p className="eyebrow">World overview</p>
            <span className={`status-dot status-${state.status}`} />
          </div>

          <dl className="metrics-grid">
            <div>
              <dt>Population</dt>
              <dd>{state.population}</dd>
            </div>
            <div>
              <dt>Continents</dt>
              <dd>{state.continentCount}</dd>
            </div>
            <div>
              <dt>World tick</dt>
              <dd>{state.tick.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Sim time</dt>
              <dd>{formatElapsedTime(state.simulationSeconds)}</dd>
            </div>
            <div>
              <dt>Season</dt>
              <dd>{titleCase(world?.weather.season ?? "spring")}</dd>
            </div>
            <div>
              <dt>Sky</dt>
              <dd>{titleCase(world?.weather.precipitation ?? "clear")}</dd>
            </div>
          </dl>

          <div className="section-rule" />

          <div className="seed-card">
            <p className="eyebrow">Foundation status</p>
            <h2>{world ? "The world is ready." : "The world is loading."}</h2>
            <p>
              This browser keeps the world on this device. Refresh keeps sim
              time, people, and events. Reset world clock starts a new
              Emberwake.
            </p>
          </div>

          <div className="system-list" aria-label="System status">
            <div>
              <span>World snapshot</span>
              <strong>{world ? "Ready" : "Loading"}</strong>
            </div>
            <div>
              <span>Simulation worker</span>
              <strong>{state.status === "error" ? "Error" : "Ready"}</strong>
            </div>
            <div>
              <span>Seed stream</span>
              <strong>{state.sample.toFixed(5)}</strong>
            </div>
          </div>
        </aside>

        <section className="world-stage" aria-label="Generated world map">
          <WorldCanvas
            onSelectBuilding={selectBuilding}
            onSelectCreature={selectCreature}
            selectedBuildingId={selectedBuildingId}
            selectedCreatureId={selectedCreatureId}
            world={world}
          />
          <div className="map-vignette" aria-hidden="true" />
          <div className="map-label map-label-north">ASHEN WEST</div>
          <div className="map-label map-label-south">GREENWATER EAST</div>
          <div className="phase-badge">
            <span>Phase 1</span>
            Living world
          </div>
        </section>

        <aside className="event-panel" aria-label="World chronicle">
          {selectedCreature && world ? (
            <CreatureInspector
              creature={selectedCreature}
              onClose={clearSelection}
              world={world}
            />
          ) : selectedBuilding && world ? (
            <BuildingInspector
              building={selectedBuilding}
              onClose={clearSelection}
              world={world}
            />
          ) : (
            <div className="inspector-empty">
              <p className="eyebrow">Map inspection</p>
              <h2>Choose a person or station</h2>
              <p>
                Click a person to inspect needs and work. Click a camp hut, fire
                pit, rack, or work stone to see what it is and what they are
                making.
              </p>
            </div>
          )}

          <div className="section-rule" />
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Living history</p>
              <h2>Chronicle</h2>
            </div>
            <span className="event-count">
              {String(world?.events.length ?? 0).padStart(2, "0")}
            </span>
          </div>

          <ol className="timeline">
            {(world?.events.slice(-6).reverse() ?? []).map((event) => (
              <li key={event.id}>
                <span className="timeline-marker" />
                <div>
                  <time>
                    Year {event.displayDate.year} · Day {event.displayDate.day}{" "}
                    · {titleCase(event.severity)}
                  </time>
                  <h3>{titleCase(event.type)}</h3>
                  <p>
                    {event.promotedBecause[0] ?? "A structured world event."}
                    {event.causeData.cause
                      ? ` Cause: ${String(event.causeData.cause)}.`
                      : ""}
                    {event.causeData.outcome
                      ? ` Outcome: ${String(event.causeData.outcome)}.`
                      : ""}
                  </p>
                </div>
              </li>
            ))}
            {(world?.events.length ?? 0) === 0 ? (
              <li className="timeline-future">
                <span className="timeline-marker" />
                <div>
                  <time>Year 0 · Waiting</time>
                  <h3>No promoted events yet</h3>
                  <p>
                    Predator attacks, injuries, deaths, and the cold snap will
                    appear here when they occur.
                  </p>
                </div>
              </li>
            ) : null}
          </ol>
        </aside>
      </section>

      <footer className="control-deck">
        <div className="playback-controls" aria-label="Simulation playback">
          <button
            className="primary-control"
            type="button"
            onClick={state.status === "running" ? pause : resume}
            aria-label={
              state.status === "running"
                ? "Pause simulation"
                : "Resume simulation"
            }
          >
            <span aria-hidden="true">
              {state.status === "running" ? "Ⅱ" : "▶"}
            </span>
          </button>

          <div className="speed-controls" aria-label="Simulation speed">
            {speedOptions.map((speed) => (
              <button
                className={state.speed === speed ? "active" : ""}
                key={speed}
                type="button"
                onClick={() => setSpeed(speed)}
              >
                {speed}×
              </button>
            ))}
          </div>
        </div>

        <div className="tick-readout">
          <span className={`status-dot status-${state.status}`} />
          <span>
            {state.status === "running" ? "Simulation active" : state.status}
          </span>
          <strong>{state.tick.toLocaleString()} ticks</strong>
        </div>

        <button className="text-control" type="button" onClick={reset}>
          Reset world clock
        </button>
      </footer>
    </main>
  );
}
